import hasProp from './hasProp';

/**
 * Map one search term to the name that's friendly to the Google geo search.
 *
 * @since  5.15.29
 *
 * @param  {string} search Search string
 *
 * @returns {string}       Possibly modified search string.
 */
export const searchMap = (search) => {
	switch (search.toLowerCase().trim()) {
		case 'berlin berlin':
		case 'berlin, berlin':
			// https://secure.helpscout.net/conversation/678080581/262131/#thread-1935278742
			search = 'Berlin, Berlin Land';
			break;
		case 'ireland':
			// If user searches for "Ireland" tell google we mean "Republic of Ireland"
			// https://secure.helpscout.net/conversation/578301802/214688/?folderId=1904465#thread-1581675165
			search = 'Republic of Ireland';
			break;
		case 'canada (ca)':
			// https://github.com/awesomemotive/project-hot-now/issues/1166
			search = 'canada';
			break;
		case 'new york':
			// https://github.com/awesomemotive/optin-monster-support/issues/510
			search = 'NY';
			break;
		default:
			break;
	}

	return search;
};

/**
 * Map one location name to the name we expect in our API.
 *
 * There are a few cases where the names provided by Google
 * do not line up with maxmind, so we modify to match.
 *
 * @since  5.15.29
 *
 * @param  {string} name Location name
 *
 * @returns {string}     Possibly replaced location name.
 */
export const map = (name) => {
	switch (name.toLowerCase().trim()) {
		case 'berlin':
		case 'land berlin':
			// https://github.com/awesomemotive/optinmonster-api-js/issues/166
			name = 'Land Berlin';
			break;
		case 'masovian voivodeship':
			// https://github.com/awesomemotive/project-hot-now/issues/862
			name = 'Mazovia';
			break;
		case 'lazio':
			// https://github.com/awesomemotive/project-hot-now/issues/876
			name = 'Latium';
			break;
		case 'prague':
			// https://github.com/awesomemotive/project-hot-now/issues/909
			name = 'Hlavni mesto Praha';
			break;
		case 'brussels':
			name = 'Brussels Capital';
			break;
		case 'valencian community':
			// https://github.com/awesomemotive/project-hot-now/issues/1157
			name = 'Valencia';
			break;
		case 'sofia city province':
			name = 'Sofia-Capital';
			break;
		case 'county dublin':
			// https://github.com/awesomemotive/optinmonster-js-library/pull/58
			name = 'Leinster';
			break;
		case 'ho chi minh city':
			// https://github.com/awesomemotive/optinmonster-js-library/issues/69
			name = 'Ho Chi Minh';
			break;
		default:
			break;
	}

	return name;
};

/**
 * Map one location name to the name we expect in our API.
 *
 * There are a few cases where the names provided by Google
 * do not line up with maxmind, so we modify to match.
 *
 * Used specifically for mis-matched localities
 *
 * @since  5.19.67
 *
 * @param  {string} localityName Locality name
 *
 * @returns {string}     Possibly replaced location name.
 */
export const localityMap = (localityName) => {
	switch (localityName.toLowerCase().trim()) {
		case 'odemis':
			// https://github.com/awesomemotive/project-hot-now/issues/512
			// Yes, I know, OE is correct and not a typo
			localityName = 'OEdemis';
			break;
		case 'frankfurt':
			// https://github.com/awesomemotive/project-hot-now/issues/2198
			localityName = 'Frankfurt am Main';
			break;
		default:
			break;
	}

	return localityName;
};

/**
 * Returns a location display name from a location object.
 *
 * @since  5.0.31
 *
 * @param  {Object|mixed} location Location object
 *
 * @returns {string} Location display name if given proper location object.
 */
export const locationDisplayName = (location) => {
	let displayName = '';

	if (hasProp(location, 'locality')) {
		let localityName = localityMap(location.locality.longName);
		displayName = displayName.concat(localityName + ', ');
	}

	if (hasProp(location, 'administrative_area_level_1')) {
		let longName = map(location.administrative_area_level_1.longName);
		displayName = displayName.concat(longName + ', ');
	}

	if (hasProp(location, 'country')) {
		if (hasProp(location.country, 'longName')) {
			displayName = displayName.concat(location.country.longName);
		}

		if (displayName) {
			displayName = displayName.concat(` (${location.country.shortName})`);
		} else {
			displayName = displayName.concat(location.country.shortName);
		}
	}

	return displayName;
};
